import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Container from '../../components/Container';
import ComponentErrorBoundary from '../../components/ComponentErrorBoundary';
import SEO from '../../lib/seo';
import { clientsApi, clientEmailsApi, assetsApi, projectsApi, formatDate, generateId } from '../../lib/adminApi';

/**
 * Admin Clients Management Page
 * @returns {React.ReactElement} Clients management interface
 */
const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientEmails, setClientEmails] = useState([]);
  const [clientAssets, setClientAssets] = useState([]);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await clientsApi.getAll();
      setClients(data);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadClientEmails = async (clientId) => {
    try {
      const emails = await clientEmailsApi.getByClientId(clientId);
      setClientEmails(emails);
    } catch (err) {
      console.error('Failed to load client emails:', err);
    }
  };

  const loadClientAssets = async (clientId) => {
    try {
      // Get all assets and projects to filter by client
      const [allAssets, allProjects] = await Promise.all([
        assetsApi.getAll(),
        projectsApi.getAll()
      ]);
      
      // Get projects for this client
      const clientProjects = allProjects.filter(project => project.client_id === clientId);
      const clientProjectIds = clientProjects.map(project => project.id);
      
      // Filter assets that belong to client's projects
      const clientAssets = allAssets.filter(asset => 
        clientProjectIds.includes(asset.project_id)
      );
      
      setClientAssets(clientAssets);
    } catch (err) {
      console.error('Failed to load client assets:', err);
    }
  };

  const handleCreateClient = async (clientData) => {
    try {
      await clientsApi.create({
        ...clientData,
        id: clientData.id || generateId('client'),
      });
      await loadClients();
      setShowCreateModal(false);
    } catch (err) {
      // Show user-friendly error message for duplicate IDs
      if (err.message.includes('already exists')) {
        alert(`❌ ${err.message}\n\nPlease choose a different Client ID and try again.`);
      } else {
        alert(`Failed to create client: ${err.message}`);
      }
    }
  };

  const handleUpdateClient = async (clientData) => {
    try {
      await clientsApi.update(editingClient.id, clientData);
      await loadClients();
      setEditingClient(null);
    } catch (err) {
      alert(`Failed to update client: ${err.message}`);
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client? This will also delete all associated projects and assets.')) {
      return;
    }

    try {
      await clientsApi.delete(clientId);
      await loadClients();
      if (selectedClient?.id === clientId) {
        setSelectedClient(null);
        setClientEmails([]);
        setClientAssets([]);
      }
    } catch (err) {
      alert(`Failed to delete client: ${err.message}`);
    }
  };

  const handleViewClient = async (client) => {
    setSelectedClient(client);
    await loadClientEmails(client.id);
    await loadClientAssets(client.id);
  };

  const ClientForm = ({ client, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      id: client?.id || '',
      display_name: client?.display_name || '',
      status: client?.status || 'active',
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <h3 className="text-lg font-semibold mb-4">
            {client ? 'Edit Client' : 'Create New Client'}
          </h3>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client ID (slug) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                placeholder="e.g., acme-realty"
                required
                disabled={!!client} // Can't change ID when editing
              />
              {!client && (
                <p className="mt-1 text-xs text-gray-500">
                  Must be unique. Use lowercase letters, numbers, and hyphens only.
                </p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Name
              </label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="e.g., Acme Realty"
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-brand-blue-600 text-white rounded-md hover:bg-brand-blue-700"
              >
                {client ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-brand-blue-600">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue-600 mx-auto mb-4"></div>
            <p className="text-white">Loading clients...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-brand-blue-600">
      <SEO 
        title="Manage Clients | LSAS Admin Portal"
        description="Manage client accounts and access permissions"
        url="/admin/clients"
      />
      
      <Header />
      
      {/* Admin Header */}
      <section className="pt-40 pb-8 bg-brand-black text-white">
        <Container>
          <div className="flex items-center justify-between">
            <div>
              <nav className="text-sm mb-2">
                <Link to="/admin" className="text-gray-400 hover:text-white">Admin</Link>
                <span className="text-gray-400 mx-2">/</span>
                <span className="text-white">Clients</span>
              </nav>
              <h1 className="text-3xl font-bold mb-2">Manage Clients</h1>
              <p className="text-gray-300">Add, edit, and manage client accounts</p>
            </div>
            <div className="flex space-x-3">
              <Link
                to="/admin"
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                ← Back to Dashboard
              </Link>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-brand-blue-600 text-white rounded-md hover:bg-brand-blue-700"
              >
                Add New Client
              </button>
            </div>
          </div>
        </Container>
      </section>

      <main className="flex-grow py-8">
        <Container>
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              <p className="font-semibold">Error loading clients:</p>
              <p>{error}</p>
              <button 
                onClick={loadClients}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Clients List */}
            <div className="lg:col-span-2">
              <ComponentErrorBoundary 
                fallbackMessage="Client list temporarily unavailable"
                componentName="ClientsList"
              >
                <div className="bg-white rounded-xl shadow-card">
                  <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold">Clients ({clients.length})</h2>
                  </div>
                  
                  {clients.length > 0 ? (
                    <div className="divide-y divide-gray-200">
                      {clients.map((client) => (
                        <div key={client.id} className="p-6 hover:bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center">
                                <h3 className="text-lg font-medium text-gray-900">
                                  {client.display_name}
                                </h3>
                                <span className={`ml-3 px-2 py-1 text-xs rounded-full ${
                                  client.status === 'active' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {client.status}
                                </span>
                              </div>
                              <p className="text-sm text-gray-500 mt-1">
                                ID: {client.id}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                Created: {formatDate(client.created_at)}
                              </p>
                            </div>
                            
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleViewClient(client)}
                                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                              >
                                View
                              </button>
                              <button
                                onClick={() => setEditingClient(client)}
                                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteClient(client.id)}
                                className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <p>No clients found</p>
                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="mt-2 text-brand-blue-600 hover:text-brand-blue-700"
                      >
                        Create your first client
                      </button>
                    </div>
                  )}
                </div>
              </ComponentErrorBoundary>
            </div>

            {/* Client Details */}
            <div>
              <ComponentErrorBoundary 
                fallbackMessage="Client details temporarily unavailable"
                componentName="ClientDetails"
              >
                <div className="bg-white rounded-xl shadow-card">
                  <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold">Client Details</h2>
                  </div>
                  
                  {selectedClient ? (
                    <div className="p-6">
                      <div className="mb-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {selectedClient.display_name}
                        </h3>
                        <p className="text-sm text-gray-500">ID: {selectedClient.id}</p>
                        <p className="text-sm text-gray-500">Status: {selectedClient.status}</p>
                        <p className="text-xs text-gray-400 mt-2">
                          Created: {formatDate(selectedClient.created_at)}
                        </p>
                      </div>

                      <div className="mb-6">
                        <h4 className="text-md font-medium text-gray-900 mb-3">
                          Authorized Emails ({clientEmails.length})
                        </h4>
                        {clientEmails.length > 0 ? (
                          <div className="space-y-2">
                            {clientEmails.map((email) => (
                              <div key={email.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <div>
                                  <p className="text-sm font-medium">{email.email}</p>
                                  <p className="text-xs text-gray-500">{email.role}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No authorized emails</p>
                        )}
                      </div>

                      <div className="mb-6">
                        <h4 className="text-md font-medium text-gray-900 mb-3">
                          Recent Assets ({clientAssets.length})
                        </h4>
                        {clientAssets.length > 0 ? (
                          <div className="space-y-2">
                            {clientAssets.slice(0, 5).map((asset) => (
                              <div key={asset.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <div>
                                  <p className="text-sm font-medium">{asset.filename}</p>
                                  <p className="text-xs text-gray-500">
                                    {asset.kind} • {asset.project_title || asset.project_id}
                                  </p>
                                </div>
                                <div className="text-xs text-gray-400">
                                  {asset.size_bytes ? `${(asset.size_bytes / 1024 / 1024).toFixed(1)} MB` : ''}
                                </div>
                              </div>
                            ))}
                            {clientAssets.length > 5 && (
                              <p className="text-xs text-gray-500 text-center">
                                +{clientAssets.length - 5} more assets
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No assets uploaded</p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <Link
                          to={`/admin/projects?client=${selectedClient.id}`}
                          className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded text-center hover:bg-blue-200"
                        >
                          View Projects
                        </Link>
                        <Link
                          to={`/admin/assets?client=${selectedClient.id}`}
                          className="px-3 py-2 text-sm bg-purple-100 text-purple-700 rounded text-center hover:bg-purple-200"
                        >
                          Manage Assets
                        </Link>
                        <Link
                          to={`/admin/client-emails?client=${selectedClient.id}`}
                          className="px-3 py-2 text-sm bg-green-100 text-green-700 rounded text-center hover:bg-green-200"
                        >
                          Manage Emails
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p>Select a client to view details</p>
                    </div>
                  )}
                </div>
              </ComponentErrorBoundary>
            </div>
          </div>
        </Container>
      </main>

      {/* Modals */}
      {showCreateModal && (
        <ClientForm
          onSubmit={handleCreateClient}
          onCancel={() => setShowCreateModal(false)}
        />
      )}

      {editingClient && (
        <ClientForm
          client={editingClient}
          onSubmit={handleUpdateClient}
          onCancel={() => setEditingClient(null)}
        />
      )}

      <Footer />
    </div>
  );
};

export default AdminClients;
